<template>
  <div>
    <CollectionPointForm
      v-if="addressStore.hasCollectionPoint"
      :api-errors="apiErrors"
      :birthdate="formValues.birthdate"
      :first-name="formValues.firstName"
      :last-name="formValues.lastName"
      :nationality="formValues.nationality"
      :phone="formValues.phone"
      :submitting="isFormDisabled"
      :with-user-information="userInformationStore.isFormRequired"
      @submit="handleCollectionPointSubmit"
    />

    <ShippingAddressForm
      v-if="!addressStore.hasCollectionPoint"
      :address="formValues"
      :api-errors="apiErrors"
      :countries="countries"
      :country="countryCode"
      :submitting="isFormDisabled"
      :with-user-information="userInformationStore.isFormRequired"
      @submit="handleShippingAddressSubmit"
    >
      <template #default="{ country }">
        <RevDivider
          v-if="cartStore.hasUnshippableItemsForCountry(country)"
          class="my-16 md:my-24"
        />

        <UnavailableShippings
          v-if="cartStore.hasUnshippableItemsForCountry(country)"
          :address-country="country"
          class="mt-32"
          :loading="isRemovingItem"
          @remove="handleUnavailableShippingsRemove"
          @update="handleUnavailableShippingsUpdate"
        />
      </template>
    </ShippingAddressForm>

    <ReassuranceItems class="mt-56" />
  </div>
</template>

<script setup>
import { useRoute, useRouter } from '#imports'
import { computed, ref } from 'vue'

import { cartAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useUserStore } from '@backmarket/nuxt-module-oauth/useUserStore'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevDivider } from '@ds/components/Divider'
import {
  getPhoneNumberInfos,
  getPhoneNumberToE164,
} from '@ds/components/InputPhone'

import { ROUTES } from '~/scopes/auth/routes'

import { CHECKOUT } from '../../routes-names'
import { useAddressStore } from '../../stores/addressStore'
import { useCartStore } from '../../stores/cartStore'
import { useLoaderStore } from '../../stores/loaderStore'
import { useUserInformationStore } from '../../stores/userInformationStore'

import CollectionPointForm from './components/CollectionPointForm/CollectionPointForm.vue'
import ShippingAddressForm from './components/ShippingAddressForm/ShippingAddressForm.vue'
import UnavailableShippings from './components/UnavailableShippings/UnavailableShippings.vue'

import ReassuranceItems from '~/scopes/reassurance/components/ReassuranceItems/ReassuranceItems'

const isRemovingItem = ref(false)
const apiErrors = ref({})
const selectedShippings = ref({})

const router = useRouter()
const route = useRoute()
const logger = useLogger()
const tracking = useTracking()

const {
  market: { countryCode },
} = useMarketplace()
const userStore = useUserStore()

const addressStore = useAddressStore()
const cartStore = useCartStore()
const userInformationStore = useUserInformationStore()
const loaderStore = useLoaderStore()

await cartStore.fetchShippings()

const address = computed(() => {
  const shippingCountry = addressStore.shipping.country
  const hasUnshippableItemsForCountry =
    shippingCountry && cartStore.hasUnshippableItemsForCountry(shippingCountry)

  if (
    shippingCountry &&
    hasUnshippableItemsForCountry &&
    !addressStore.shippableCountries.some(
      (country) => country.countryCode === addressStore.shipping.country,
    )
  ) {
    return { country: countryCode }
  }

  return addressStore.hasCollectionPoint
    ? addressStore.collectionPoint
    : addressStore.shipping
})

const formValues = computed(() => {
  const phone = address.value.phone || userStore.user.phone || ''
  const countryDialInCode =
    address.value.countryDialInCode || userStore.user.countryDialInCode || ''

  return {
    ...address.value,
    country: address.value.country || countryCode || '',
    firstName: address.value.firstName || userStore.user.firstName || '',
    lastName: address.value.lastName || userStore.user.lastName || '',
    phone:
      phone && countryDialInCode
        ? getPhoneNumberToE164(countryDialInCode, phone)
        : phone,
    nationality: address.value.nationality || countryCode || '',

    ...insertIf(userInformationStore.isFormRequired, {
      nationality: userInformationStore.nationality || countryCode,
      birthdate: userInformationStore.birthdate || '',
      nationalId: userInformationStore.nationalId,
    }),
  }
})

const countries = computed(() => {
  return [...addressStore.shippableCountries]
    .map(({ name, countryCode: shippableCountryCode }) => ({
      value: shippableCountryCode,
      label: name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const isFormDisabled = computed(() => {
  return loaderStore.isEnabled || isRemovingItem.value
})

const allShippingsSelected = computed(() => {
  return Object.values(selectedShippings.value).every(Boolean)
})

const safelyFetchShippings = async () => {
  try {
    await cartStore.fetchShippings()
  } catch (error) {
    const next = route.fullPath

    switch (error.status_code) {
      case 401:
        // User has been disconnected
        router.push({
          name: ROUTES.AUTH.LOGIN,
          query: { next },
        })
        break
      case 403:
        // HTTP 403 can occur when user is merchant or staff. We just ignore
        // that error here.
        break
      default:
        throw error
    }
  }
}

const handleUnavailableShippingsUpdate = (shippings) => {
  selectedShippings.value = shippings
}

const handleUnavailableShippingsRemove = async (item) => {
  isRemovingItem.value = true

  try {
    await $httpFetch(cartAPI.postUpdateQuantity, {
      body: {
        action: 'delete',
        listingId: item.listingId,
        newQuantity: item.quantity,
        listingPrice: item.price,
      },
    })

    await cartStore.fetchCart()
    await safelyFetchShippings()
  } catch (error) {
    // Do nothing, we monitor errors instead
  }

  isRemovingItem.value = false
}

const handleApiError = (error) => {
  if (error instanceof Error) {
    logger.error('[CHECKOUT] Failed to submit shipping form', { error })
  } else {
    // Report errors from API.
    // Ex: { postalCode: ['ZIP code is invalid'] }
    // We display only the first one
    Object.assign(
      apiErrors.value,
      Object.keys(error).reduce(
        (acc, key) => ({
          ...acc,
          [key]: error[key].length > 0 ? error[key][0] : '',
        }),
        {},
      ),
    )
  }
}

const handleCollectionPointSubmit = async ({
  firstName,
  lastName,
  phone,
  birthdate,
  nationality,
}) => {
  loaderStore.enable()

  tracking.trackClick({
    name: '3_shipping-new-edit_cta_continue',
    zone: 'funnel',
  })

  try {
    const { dial, nationalNumber } = getPhoneNumberInfos(phone, countryCode)

    if (isEmpty(nationalNumber)) {
      logger.info('[CHECKOUT] Failed to convert phone in shipping form', {
        phone,
      })
    }

    await addressStore.saveCollectionPoint({
      address: {
        ...address.value,
        firstName,
        lastName,
        phone: nationalNumber,
        countryDialInCode: dial,
        ...insertIf(userInformationStore.isFormRequired, {
          birthdate,
          nationality,
        }),
      },
      ...insertIf(userInformationStore.isFormRequired, {
        formType: userInformationStore.formType,
      }),
    })

    // Reload checkout store
    await cartStore.fetchCart()

    router.push({
      name: CHECKOUT.BILLING_ADDRESS,
    })
  } catch (error) {
    loaderStore.disable()
    handleApiError(error)
  }
}

const handleShippingAddressSubmit = async (values, sameAsShipping) => {
  apiErrors.value = {}

  if (
    !allShippingsSelected.value &&
    cartStore.hasUnshippableItemsForCountry(values.country)
  ) {
    return
  }

  loaderStore.enable()

  try {
    tracking.trackClick({
      name: '3_shipping-new-edit_cta_continue',
      zone: 'funnel',
    })

    // If needed, save shippings
    if (
      !isEmpty(selectedShippings.value) &&
      cartStore.hasUnshippableItemsForCountry(values.country)
    ) {
      await $httpFetch(cartAPI.postShippings, {
        body: Object.entries(selectedShippings.value).map(
          ([itemId, shipping]) => ({
            listingId: Number(itemId),
            shippingId: shipping.shippingId,
            shipperDisplay: shipping.shipperDisplay,
          }),
        ),
      })
    }

    const { dial, nationalNumber } = getPhoneNumberInfos(
      values.phone,
      values.country,
    )

    if (isEmpty(nationalNumber)) {
      logger.info('[CHECKOUT] Failed to convert phone in shipping form', {
        phone: values.phone,
      })
    }

    await addressStore.saveAddress({
      isShipping: true,
      isBilling: sameAsShipping,
      ...insertIf(userInformationStore.isFormRequired, {
        formType: userInformationStore.formType,
      }),
      address: {
        ...values,
        phone: nationalNumber,
        countryDialInCode: dial,
      },
    })

    // Reload checkout store
    await cartStore.fetchCart()

    router.push({
      name: sameAsShipping ? CHECKOUT.PAYMENT : CHECKOUT.BILLING_ADDRESS,
    })
  } catch (error) {
    loaderStore.disable()
    handleApiError(error)
  }
}
</script>
